/**
 * Scripted performance run (`npm run perf`).
 *
 * Activated by `?perf=<seconds>`. It lets the scene run untouched for a warm-up period, then
 * measures for the requested duration and reports average fps, the 1 %-low, the per-subsystem
 * millisecond breakdown, draw calls and the heap delta — the exact figures the budgets in
 * CLAUDE.md are written in.
 *
 * ## Why there is a warm-up at all, and why it is 10 s
 *
 * The opening seconds of a session are not the thing the 60 fps floor is about. The first frame
 * that draws terrain compiles its pipeline; the first frame that draws water compiles another;
 * several hundred tiles arrive from a cold network and are uploaded; the governor has not yet
 * seen enough frames to know what the machine can do. Left inside the window, those frames set
 * the 1 %-low all by themselves and the number stops describing steady flight. Ten seconds is
 * long enough for pipeline compilation and the initial tile burst to be behind us, and short
 * enough that a perf run is still a minute of work.
 *
 * ## Instrumenting the stutter rather than guessing at it
 *
 * A 1 %-low figure on its own says "something took 40 ms" and nothing else, which is an
 * invitation to guess. So every frame is checked against the running worst list, and the twenty
 * longest are kept **with what happened in them**: the per-channel breakdown, how many tiles were
 * adopted, how many bytes went to the GPU, how many tile materials were built for the first time,
 * and how much of the frame is unattributed — the signature of a garbage collection or a driver
 * doing something behind our back. The heap is sampled once a second over the same window, so a
 * leak shows up as a staircase rather than being inferred from a single delta.
 *
 * The result is published three ways so it is usable from anywhere: `console.table`, a
 * `window.__SKYSIM_PERF__` global for a driver to read, and a `sendBeacon` POST to
 * `/__skysim_perf` which `tools/perf.mjs` listens for.
 *
 * `updateFrame(alpha)`: called from the loop's after-frame slot.
 */

import {
  ALLOCATION_SLOT_NAMES,
  CHANNEL_BUDGET_MS,
  CHANNEL_NAMES,
  Channel,
  type AllocationProbe,
  type Profiler,
  heapUsedBytes,
} from '../core/profiler.js';
import { PRODUCT_NAME } from '../brand.js';
import type { QualityManager } from '../core/quality.js';
import type { RendererBackend } from '../render/renderer.js';

export const PERF_ENDPOINT = '/__skysim_perf';

/** How many of the worst frames are kept and reported. */
const LONG_FRAME_COUNT = 20;
/**
 * Heap sampling interval [ms].
 *
 * Fine enough to resolve the collector's sawtooth, which is the whole point: sampled once a
 * second, a heap that is filling with short-lived garbage and being collected looks exactly like
 * a heap that is leaking, and the two want completely different fixes. Each reported second
 * carries the *floor* as well as the peak, and it is the floor that tells you which one you have
 * — a rising floor is retention, a flat floor under a rising peak is the collector doing its job.
 */
const HEAP_SAMPLE_INTERVAL_MS = 250;
/** Default settling time before measurement starts [s]. */
const DEFAULT_WARMUP_S = 10;
/** How many times a lost window may restart the warm-up before the run gives up and says so. */
const MAX_WARMUP_RESTARTS = 3;

export interface PerfMetric {
  metric: string;
  value: number;
  budget: number | null;
  unit: string;
  /** True when the metric is inside its budget, null when there is no budget. */
  pass: boolean | null;
}

/**
 * Cumulative streaming counters, sampled once a frame so the harness can attribute a long frame
 * to what the streamer did in it. Cumulative rather than per-frame so the harness owns the
 * diffing and the streamer needs no knowledge that a perf run is happening.
 */
export interface StreamingMarks {
  /** Tiles adopted into the scene since start-up. */
  tilesAdopted: number;
  /** Bytes of geometry and imagery handed to the GPU since start-up. */
  uploadedBytes: number;
  /** Tile materials built since start-up. Each one is a potential first-use pipeline build. */
  materialsCreated: number;
  /** Finished tiles waiting for an upload slot right now. */
  pendingAdoptions: number;
  /** Tiles resident right now. */
  resident: number;
}

/** One of the worst frames of the run, with everything known about why it was slow. */
export interface LongFrame {
  /** When it happened, seconds into the measurement window. */
  atS: number;
  frameMs: number;
  physicsMs: number;
  terrainMs: number;
  renderCpuMs: number;
  /** Frame time not accounted for by any channel — GC, driver stalls, compositor waits. */
  gapMs: number;
  /**
   * {@link renderCpuMs}, split into the three passes it actually contains.
   *
   * `renderCPU` covers our interpolation *and* the whole of `PostChain.render` — the scene, the
   * cloud raymarch and the post chain — so a long frame blamed on it names a call site rather than
   * a cause. These say which pass. Zero when the phase timer was off, or when the post chain is
   * disabled and the caller rendered straight to the canvas.
   */
  sceneMs: number;
  cloudsMs: number;
  postMs: number;
  /**
   * Floating-origin rebases since the previous frame — almost always 0, occasionally 1.
   *
   * One column, because one measurement earned it. A rebase translates every object in the scene
   * by ten kilometres at once, and the frame that follows costs 350–700 ms, effectively all of it
   * in the scene pass and about 85 % of that in the shadow pass. Nothing else in the itemisation
   * could see it: no tile is adopted, no material is built, no shader is compiled, and the
   * unattributed gap stays small, so the frame reads as "render" with no cause. See the README.
   */
  rebases: number;
  tilesAdopted: number;
  uploadedBytes: number;
  materialsCreated: number;
  /**
   * Shader programs compiled during this frame.
   *
   * The single most useful number on this table, because a recompile is invisible in every other
   * column: it shows up as render CPU with no draw calls, no tiles and no materials to blame it
   * on. Anything above zero here means the frame stopped to build pipelines, which on WebGPU is
   * the most expensive thing a frame can do — one light appearing cost 173 ms of it.
   */
  programsCompiled: number;
  /** The harness' best single-word explanation. */
  suspect: string;
}

/**
 * One second of heap history: the peak, and — the number that matters — the floor.
 *
 * A steady scene allocates and collects continuously, so the peak sawtooths and says nothing.
 * The floor is what the collector could not reclaim, and a floor that climbs is the definition
 * of a leak.
 */
export interface HeapSample {
  atS: number;
  /** Highest sample in this second [MB]. */
  heapMb: number;
  /** Lowest sample in this second [MB] — what survived collection. */
  floorMb: number;
  resident: number;
  /**
   * JS heap held by resident terrain tiles at this sample [MB] — retained *by design*.
   *
   * Subtracted from {@link floorMb} before growth is measured. See {@link heapGrowthFromTrace}
   * for why a rule that does not do this cannot tell a leak from a cache filling up.
   *
   * Zero when there is no terrain to ask (a unit test, or a scene without a tile stream), in
   * which case the rule degrades exactly to the raw-floor rule it replaced.
   */
  tileHeapMb: number;
}

/**
 * A drop in the per-second heap floor large enough to be a major collection [MB].
 *
 * Well above the noise in a floor reading, well below the tens of megabytes a real mark-compact
 * reclaims on a scene this size.
 */
export const HEAP_COLLECTION_DROP_MB = 8;

/**
 * Consecutive settled frames before the warm-up will hand over to the measurement.
 *
 * Half a second at 120 fps. Long enough that a gap between fetch batches cannot be mistaken for a
 * finished scene, short enough that a warm cache is not made to wait.
 */
const SETTLED_FRAMES = 60;

/**
 * How much longer than the requested warm-up the harness will wait for the terrain to settle.
 *
 * Generous, because a cold canyon is a few hundred tiles over the network, and bounded, because a
 * scene that never settles still has to be measurable — the report says which happened.
 */
const MAX_WARMUP_MULTIPLE = 12;

/** Below this many samples there is no trace worth reasoning about. */
export const MIN_HEAP_TRACE_SAMPLES = 4;

/**
 * How often the measurement window says it is still alive [ms].
 *
 * `announce` already covers "did the page load" and "did the warm-up end". It did not cover the
 * failure mode that actually happened: a run that reached `measuring` and then produced nothing at
 * all for 255 s, at which point the runner gave up with "last phase reached was measuring" — which
 * narrows a four-minute silence to a forty-five-second window and stops there. A stall and a
 * merely-slow scene are indistinguishable from outside, and they want opposite responses.
 *
 * So the window reports its own progress. Five seconds is frequent enough to bracket a stall to
 * within one beat and rare enough that the beacons are not themselves a measurable cost.
 */
const HEARTBEAT_INTERVAL_MS = 5_000;

export interface HeapGrowth {
  /**
   * Megabytes. **Only means "what survived collection" when {@link conclusive} is true** —
   * otherwise it is the span of the floor across the window, reported for information.
   */
  mb: number;
  /** Major collections found in the window. */
  collections: number;
  /** True when at least two collections were caught, so there is a trend to measure. */
  conclusive: boolean;
  /**
   * How much the tile cache's own retained heap moved between the same two floors [MB].
   *
   * The confound, made visible. This is the number that used to be silently inside {@link mb};
   * reported beside it so a reader can see how much of the window was spent filling the cache.
   * Positive when the cache grew across the window, negative when it was evicting.
   */
  tileHeapMb: number;
}

/**
 * Did the heap grow, over a run's worth of per-second samples?
 *
 * ## Why this is its own exported function
 *
 * The same reason `tools/hostLoad.mjs` is its own module: it is the part with a *rule* in it.
 * `PerfHarness` cannot be exercised without a profiler, a quality manager, a renderer and a
 * browser, but this is pure arithmetic over a list of numbers — and it is the piece that will
 * silently rot, because a threshold that stops firing and a threshold that fires constantly look
 * identical from outside a single run. `perfHarness.test.ts` pins both edges against a scripted
 * sawtooth rather than against whatever the machine happened to do.
 *
 * ## The rule
 *
 * A floor is what survived a collection, so growth is the distance between one post-collection
 * floor and another. Anything else measures where in the sawtooth the window happened to open and
 * close.
 *
 * The previous version compared the lowest second in the first quarter of the run against the
 * lowest in the last, which is the right idea and the wrong implementation: it assumes every
 * quarter *contains* a collection. A canyon run sawtooths between about 92 MB and 180 MB with
 * roughly eleven seconds between major collections, so a 45 s window catches three and whether the
 * final quarter holds one is close to a coin toss. On the run that exposed it, it did not — the
 * last collection landed at t ≈ 29 s and the final eleven seconds were pure rising edge, so it
 * compared a real floor of 95.1 MB against the lowest point on a ramp at 124.7 MB and reported
 * 29.6 MB of growth on a heap that was not growing at all.
 *
 * With fewer than two collections there is no trend to measure and this says so, because a number
 * with no verdict is more useful than a verdict with no basis.
 *
 * ## The cache-fill blind spot, and why the floor alone is not enough
 *
 * Measuring floor-to-floor is necessary and it is not sufficient, and the gap was found the only
 * way a gap like this ever is: by running the same build twice. Two 60 s canyon runs reported
 * **−6.15 MB (pass)** and **+16.49 MB (FAIL)**. Neither run leaked. The failing run's floors were
 * `78.5 91.0 81.3 91.3 93.4 94.9`, and 94.9 − 78.5 = 16.4 is exactly what it printed — but the
 * first of those landed at t = 1 s with 241 tiles resident and the last at t = 59 s with 330, the
 * cache having climbed to its budget in between.
 *
 * **A cache filling toward its budget is retained memory by design.** It survives every collection,
 * because it is supposed to. So a floor-to-floor rule over a window that opens before the cache is
 * full is guaranteed to report growth, and the amount it reports is a measurement of how empty the
 * cache happened to be when the clock started — which is why the same build can score −6 and +16.
 *
 * The fix is to subtract it. Each sample carries {@link HeapSample.tileHeapMb}, the JS heap held by
 * resident tiles at that moment (`TerrainStats.heapBytes` — the tiles' own geometry arrays, summed
 * exactly rather than estimated), and growth is measured between *adjusted* floors. What is left is
 * retained memory that is **not** accounted for, which is the only thing the flat-heap criterion in
 * non-negotiable #3 was ever asking about.
 *
 * Two details worth stating, because both are ways this could have been got wrong:
 *
 *   - **Collections are still detected on the raw floor.** A collection is a drop in the heap the
 *     collector actually made; a large eviction is not one. Detecting on the adjusted floor would
 *     let a burst of evictions masquerade as a mark-compact and invent floors that never happened.
 *   - **Only the tiles' own arrays are subtracted, not their GPU footprint.** `cacheBytes` is the
 *     LRU's budget figure and counts texture memory, which `usedJSHeapSize` never saw; subtracting
 *     it would over-correct and start hiding real leaks. `heapBytes` is the part the collector can
 *     see, which is the part that needs removing. (An `ImageBitmap` is not in either — see the note
 *     on `TerrainTile.imagery`.)
 *
 * ## What this does *not* fix, and one thing that was tried
 *
 * Subtracting the cache removes the dominant confound and does not make the metric quiet. Measured
 * over five 60 s canyon runs on this host, the residual scattered between **+1.8 MB and +14.1 MB**
 * while the excluded cache term stayed steady at 9.7–13.2 MB. So the subtraction is doing exactly
 * what it claims — the term it removes is large, stable and correctly attributed — and something
 * else is still moving.
 *
 * The likely candidate, unproven: every tile drawn for the first time makes three build a
 * `RenderObject` and its bind groups, which are retained on three's side where
 * `TerrainStats.heapBytes` cannot see them. (They are also the ~10 ms per first draw the remaining
 * 1 %-low problem is made of, so it would be the same phenomenon wearing a different hat.)
 *
 * **Gating the first floor on a settled resident count was tried and is not shipped.** It is the
 * fix this note originally named, the rationale survives contact with the code, and it is
 * *unit-testable and was tested* — and three runs with it scored 14.09 / 8.09 / 1.84 MB against
 * 4.61 / 7.09 without, which is not an improvement. There is a mechanism for that rather than just
 * noise: `resident` settles around t = 33 s on the canyon run, so the gate throws away the first
 * half of the trace and leaves two or three collections instead of five or six. It trades a
 * systematic bias for variance, on a window too short to afford it.
 *
 * The honest state, therefore: the named confound is gone and reported; the metric is still noisier
 * than its 8 MB budget on a host at 4.5 runnable threads per core, and this host cannot currently
 * tell whether that is the renderer or the machine. Read a heap verdict together with the
 * `tile heap Δ (excluded)` row, and do not chase a single run's figure — the project has already
 * paid for that lesson twice.
 */
export function heapGrowthFromTrace(trace: readonly HeapSample[]): HeapGrowth {
  if (trace.length < MIN_HEAP_TRACE_SAMPLES) {
    return { mb: 0, collections: 0, conclusive: false, tileHeapMb: 0 };
  }

  // Detected on the raw floor: a collection is a drop the *collector* made. See above.
  const floors: HeapSample[] = [];
  for (let i = 1; i < trace.length; i++) {
    if (trace[i - 1].floorMb - trace[i].floorMb >= HEAP_COLLECTION_DROP_MB) {
      floors.push(trace[i]);
    }
  }

  if (floors.length < 2) {
    let lowest = Number.POSITIVE_INFINITY;
    let highest = Number.NEGATIVE_INFINITY;
    for (const sample of trace) {
      const adjusted = sample.floorMb - sample.tileHeapMb;
      lowest = Math.min(lowest, adjusted);
      highest = Math.max(highest, adjusted);
    }
    return {
      mb: highest - lowest,
      collections: floors.length,
      conclusive: false,
      tileHeapMb: trace[trace.length - 1].tileHeapMb - trace[0].tileHeapMb,
    };
  }

  // …but measured on the adjusted one: what survived collection *and* is not the cache.
  const first = floors[0];
  const last = floors[floors.length - 1];
  return {
    mb: last.floorMb - last.tileHeapMb - (first.floorMb - first.tileHeapMb),
    collections: floors.length,
    conclusive: true,
    tileHeapMb: last.tileHeapMb - first.tileHeapMb,
  };
}

/**
 * The worst frame in which the floating origin rebased [ms], or 0 if none did.
 *
 * ## Why this is a budget and not a diagnostic
 *
 * A rebase used to be the single worst frame in the simulator — 350–730 ms of it, effectively all
 * in the scene pass and about 85 % of that in the shadow pass — and nothing else in the frame
 * itemisation could see it. No tile is adopted, no material is built, no shader is compiled, and
 * the unattributed gap stays small, so the frame reads as "render" with no cause. The
 * {@link LongFrame.rebases} column exists because that attribution had to be *added* before the
 * problem could even be named, and once a column can attribute a cost it can enforce a budget on
 * it.
 *
 * `ORIGIN_REBASE_DISTANCE_M` at 500 m is what made the cost survivable — the frame's cost scales
 * with how far objects moved, superlinearly, so twenty small hops are five times cheaper in total
 * than one big one. That is a *constant* holding the line, and a constant with no test on it is a
 * regression waiting for someone to reason plausibly about FP32 precision and raise it back to
 * 10 km. `geo.test.ts` pins the constant; this pins the behaviour the constant buys, which is the
 * part that actually matters.
 *
 * ## The 40 ms bound
 *
 * A rebase frame is allowed to be slower than an ordinary one — it does real work, atomically —
 * but it must stay inside the range where it costs a dropped frame rather than a visible hitch.
 * Measured on a 60 s canyon run at 500 kt: 771 ms at the old 10 km threshold, 41.9 ms at 500 m,
 * 65.1 ms at 250 m. So 40 ms is a bound the shipped configuration sits at rather than comfortably
 * inside, which is the right place for it — it fails on the way back up, not after.
 *
 * Reads the long-frame table rather than every frame, which is sound because the table keeps the
 * twenty *worst* frames: any frame slow enough to breach this bound is in it by construction, and
 * a rebase frame too fast to make the table is by definition not the worst one.
 */
export function worstRebaseFrameMs(frames: readonly LongFrame[]): number {
  let worst = 0;
  for (const frame of frames) {
    if (frame.rebases > 0 && frame.frameMs > worst) worst = frame.frameMs;
  }
  return worst;
}

/** The bound {@link worstRebaseFrameMs} is judged against [ms]. See that function for why 40. */
export const REBASE_FRAME_BUDGET_MS = 40;

/**
 * The row this budget prints under. Exported because `tools/hostLoad.mjs` has to match it.
 *
 * A constant rather than a literal repeated at both ends, because the literal-at-both-ends version
 * of exactly this arrangement has already shipped a bug twice — see `WALL_CLOCK_METRICS`. The
 * build-time script still cannot import this (it is ESM JavaScript reading a TypeScript module),
 * so the duplication survives; what does not survive is the *test* restating it, which is what let
 * the first one through.
 */
export const REBASE_FRAME_METRIC = 'worst rebase frame';

export interface PerfResult {
  scene: string;
  /** Set when the run was abandoned rather than completed, with the reason. */
  abortedBecause?: string;
  backend: RendererBackend;
  tier: string;
  durationS: number;
  warmupS: number;
  frames: number;
  averageFps: number;
  onePercentLowFps: number;
  averageFrameMs: number;
  physicsMs: number;
  terrainMs: number;
  renderCpuMs: number;
  drawCalls: number;
  triangles: number;
  heapDeltaMb: number;
  /** Major collections seen in the window. Under two and {@link heapDeltaMb} carries no verdict. */
  heapCollections?: number;
  /**
   * False when the measurement window opened on a scene that was still streaming, because the
   * warm-up hit its cap. The wall-clock figures are then partly a measurement of the network.
   */
  warmupSettled?: boolean;
  /** Per-channel allocation, when `?allocprobe=1` was passed. Lower bounds; see `AllocationProbe`. */
  allocation?: AllocationBreakdown[];
  longFrames: LongFrame[];
  heapTrace: HeapSample[];
  metrics: PerfMetric[];
  passed: boolean;
}

/** Read the requested run length from the query string, or null when not a perf run. */
export function requestedPerfSeconds(search: string = globalThis.location?.search ?? ''): number | null {
  const raw = new URLSearchParams(search).get('perf');
  if (raw === null) return null;
  const seconds = Number.parseFloat(raw);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 60;
}

/** Read the requested warm-up from the query string; {@link DEFAULT_WARMUP_S} when absent. */
export function requestedPerfWarmupSeconds(
  search: string = globalThis.location?.search ?? '',
): number {
  const raw = new URLSearchParams(search).get('warmup');
  if (raw === null) return DEFAULT_WARMUP_S;
  const seconds = Number.parseFloat(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : DEFAULT_WARMUP_S;
}

/** Per-channel allocation, when `?allocprobe=1` asked for it. */
export interface AllocationBreakdown {
  channel: string;
  bytesPerFrame: number;
}

export interface PerfHarnessOptions {
  profiler: Profiler;
  quality: QualityManager;
  backend: RendererBackend;
  durationS: number;
  sceneName?: string;
  /** Terrain counters, folded into the report so streaming health is measured too. */
  terrainStats?: () => {
    cacheBytes: number;
    cacheMaxBytes: number;
    /**
     * JS heap held by resident tiles [bytes] — retained by design, so the heap rule subtracts it.
     *
     * Distinct from {@link cacheBytes}, which is the LRU's budget figure and includes GPU memory
     * `usedJSHeapSize` never saw. See {@link heapGrowthFromTrace}.
     */
    heapBytes: number;
    resident: number;
    failed: number;
    /** Tiles being fetched or decoded right now — the signal the warm-up gate waits on. */
    loading: number;
    /** Tiles queued for fetch but not yet started. */
    queued: number;
  };
  /** Per-frame streaming marks, for attributing the long frames. */
  streamingMarks?: () => StreamingMarks;
  /**
   * The render step's three passes [ms], for attributing a long frame *inside* `render()`.
   *
   * Read only when a frame is being kept, so the object handed back may be the caller's own
   * scratch — see {@link LongFrame.sceneMs}.
   */
  renderPhases?: () => { sceneMs: number; cloudsMs: number; postMs: number };
  /** Cumulative floating-origin rebases. Differenced per frame; see {@link LongFrame.rebases}. */
  originRebases?: () => number;
  /** Settling time before measurement starts [s] — pipeline compilation, the first tile burst. */
  warmupS?: number;
  /** The allocation probe, when `?allocprobe=1` built one. Reset when measurement starts. */
  allocationProbe?: AllocationProbe;
  onComplete?: (result: PerfResult) => void;
}

export class PerfHarness {
  private readonly options: Required<
    Omit<PerfHarnessOptions, 'onComplete' | 'terrainStats' | 'streamingMarks' | 'allocationProbe' | 'renderPhases' | 'originRebases'>
  > &
    Pick<PerfHarnessOptions, 'onComplete' | 'terrainStats' | 'streamingMarks' | 'allocationProbe' | 'renderPhases' | 'originRebases'>;

  private startedAtMs = -1;
  private measuringFromMs = -1;
  private heapBaselineBytes = 0;
  private framesAtStart = 0;
  private peakDrawCalls = 0;
  private peakTriangles = 0;
  private finished = false;
  private resultValue: PerfResult | null = null;
  private wasHidden = false;
  private warmupRestarts = 0;

  /** Previous frame's timestamp and marks, so this frame's costs are a difference. */
  private lastFrameAtMs = -1;
  private readonly previousMarks: StreamingMarks = emptyMarks();
  /** Program count at the previous frame, or −1 before the first reading. */
  private previousProgramCount = -1;
  /** Rebase count at the previous frame, or −1 before the first reading. */
  private previousRebaseCount = -1;
  /** Consecutive frames with nothing pending and a steady resident count. */
  private settledFrames = 0;
  private settledResident = -1;
  /** False when the warm-up ended on the cap rather than on the scene settling. */
  private warmupSettledValue = true;
  /** Hard ceiling on the warm-up, so a scene that never settles is still measurable [ms]. */
  private readonly maxWarmupMs: number;

  /** Worst frames so far, ascending by `frameMs`, at most {@link LONG_FRAME_COUNT}. */
  private readonly longFrames: LongFrame[] = [];
  private readonly heapTrace: HeapSample[] = [];
  private nextHeapSampleMs = 0;
  /** When the next heartbeat is due, and what the window has seen since the last one. */
  private nextHeartbeatMs = 0;
  private framesAtHeartbeat = 0;
  private worstSinceHeartbeatMs = 0;
  /** Sub-second accumulator, so each reported second carries a real peak and a real floor. */
  private bucketPeakMb = 0;
  private bucketFloorMb = Infinity;
  private bucketEndsAtMs = 0;

  constructor(options: PerfHarnessOptions) {
    this.options = {
      sceneName: 'placeholder',
      warmupS: DEFAULT_WARMUP_S,
      ...options,
    };
    this.maxWarmupMs = this.options.warmupS * MAX_WARMUP_MULTIPLE * 1000;
  }

  get done(): boolean {
    return this.finished;
  }

  get result(): PerfResult | null {
    return this.resultValue;
  }

  /** True once the warm-up is over and frames are being counted. */
  get measuring(): boolean {
    return this.measuringFromMs >= 0 && !this.finished;
  }

  /**
   * The page stopped being visible. Returns true when the run has been abandoned because of it.
   *
   * A backgrounded tab gets no `requestAnimationFrame` callbacks at all, so a run that loses
   * focus does not go slowly — it stops, and the runner sits there until its timeout with
   * nothing to say. What happens next depends on when it happened:
   *
   *   - **During the measurement window** the run is over. Frames are missing from the middle of
   *     it and no honest figure can be recovered, so it abandons itself and says why.
   *   - **During the warm-up** nothing has been measured yet, so it simply starts the warm-up
   *     again when the window comes back. Windows get shuffled around at start-up — a browser
   *     launching, a terminal coming forward — and throwing the run away for that would make the
   *     tool useless exactly when it is most convenient to use.
   */
  markHidden(): boolean {
    this.wasHidden = true;
    if (this.measuringFromMs < 0) {
      // Warm-up only. Start it again — but not forever: a window that keeps flapping would
      // otherwise leave the runner waiting in silence, which is the failure mode the whole
      // abort path exists to eliminate.
      if (++this.warmupRestarts <= MAX_WARMUP_RESTARTS) {
        this.startedAtMs = -1;
        return false;
      }
      this.abort(
        `the window lost focus ${this.warmupRestarts} times before measurement could start — ` +
          'keep it in the foreground, or use --dedicated',
      );
      return true;
    }
    this.abort(
      'the window lost focus during the measurement window — a background tab gets no ' +
        'animation frames, so keep it in the foreground, or use --dedicated',
    );
    return true;
  }

  /** True when the window lost focus during the run, so the numbers cannot be trusted. */
  get interrupted(): boolean {
    return this.wasHidden;
  }

  /** Progress through the run in [0, 1], for the status pill. */
  progress(nowMs: number): number {
    if (this.startedAtMs < 0) return 0;
    const total = (this.options.warmupS + this.options.durationS) * 1000;
    return Math.min(1, (nowMs - this.startedAtMs) / total);
  }

  /**
   * Abandon the run and say why.
   *
   * Reported as a failure with everything gathered so far, rather than as silence: a runner that
   * times out tells you nothing, and the most common reason a perf run does not finish is the
   * most boring one — the window went behind something.
   */
  abort(reason: string): void {
    if (this.finished) return;
    this.finished = true;
    const result: PerfResult = {
      ...blankResult(this.options.sceneName, this.options.backend, this.options.quality.tier),
      abortedBecause: reason,
      warmupS: this.options.warmupS,
      longFrames: this.longFrames,
      heapTrace: this.heapTrace,
    };
    this.resultValue = result;
    publish(result);
    this.options.onComplete?.(result);
  }

  /** Called once per frame from the loop's after-frame slot. */
  /**
   * @param programCount Shader programs the renderer has compiled so far, or −1 when the caller
   *   cannot see it. Differenced frame to frame; see {@link LongFrame.programsCompiled}.
   */
  updateFrame(nowMs: number, drawCalls: number, triangles: number, programCount = -1): void {
    if (this.finished) return;

    if (this.startedAtMs < 0) {
      this.startedAtMs = nowMs;
      announce('started', {
        backend: this.options.backend,
        tier: this.options.quality.tier,
        warmupS: this.options.warmupS,
        durationS: this.options.durationS,
      });
      return;
    }

    /*
     * The warm-up is over when the clock says so **and the terrain has stopped arriving**.
     *
     * A fixed ten seconds was the right idea for pipeline compilation and the wrong one for tile
     * streaming. `--dedicated` uses a fresh temporary profile by design, so every run downloads
     * the scene cold, and a canyon run at 500 kt through terrain nobody has cached was observed
     * with 236 tiles visible against 2 resident, 243 in flight and a single 22,470 ms frame — a
     * measurement window opened in the middle of that measures the network, not the renderer.
     * Three runs simply never reported at all.
     *
     * So the gate is a condition rather than a countdown: nothing pending, the resident count
     * holding still, for {@link SETTLED_FRAMES} consecutive frames. {@link maxWarmupMs} caps it,
     * because a scene that never settles must still be measurable — and when the cap is what
     * ended the warm-up the report says so rather than quietly presenting the numbers.
     */
    const warmupEndsMs = this.startedAtMs + this.options.warmupS * 1000;
    if (nowMs < warmupEndsMs) return;
    if (this.measuringFromMs < 0 && !this.streamingSettled(nowMs)) return;

    if (this.measuringFromMs < 0) {
      // Warm-up over: discard everything so far and start clean.
      this.measuringFromMs = nowMs;
      this.lastFrameAtMs = nowMs;
      // The warm-up's allocation is pipeline compilation and the first tile burst — our own load,
      // and not what the report is about.
      this.options.allocationProbe?.reset();
      this.heapBaselineBytes = heapUsedBytes();
      this.framesAtStart = this.options.profiler.frameCount;
      this.nextHeapSampleMs = nowMs;
      this.nextHeartbeatMs = nowMs + HEARTBEAT_INTERVAL_MS;
      this.framesAtHeartbeat = this.framesAtStart;
      this.bucketEndsAtMs = nowMs;
      this.readMarks(this.previousMarks);
      this.options.profiler.resetWindow();
      this.sampleHeap(nowMs);
      announce('measuring', {
        afterS: (nowMs - this.startedAtMs) / 1000,
        settled: this.warmupSettledValue,
      });
      return;
    }

    if (drawCalls > this.peakDrawCalls) this.peakDrawCalls = drawCalls;
    if (triangles > this.peakTriangles) this.peakTriangles = triangles;

    this.recordFrame(nowMs, programCount);
    if (nowMs >= this.nextHeapSampleMs) {
      this.nextHeapSampleMs = nowMs + HEAP_SAMPLE_INTERVAL_MS;
      this.sampleHeap(nowMs);
    }
    this.heartbeat(nowMs);

    if (nowMs - this.measuringFromMs >= this.options.durationS * 1000) {
      this.finish(nowMs);
    }
  }

  /**
   * Say the window is still running, and what it has seen since the last time it said so.
   *
   * The `worst` figure is the point: a run that stops reporting after a beat saying 8 ms was
   * killed by something outside the loop, and one that stops after a beat saying 900 ms was killed
   * by something inside it. Those are different investigations, and until this existed the runner
   * could not tell them apart. See {@link HEARTBEAT_INTERVAL_MS}.
   */
  private heartbeat(nowMs: number): void {
    if (nowMs < this.nextHeartbeatMs) return;
    const frames = this.options.profiler.frameCount;
    announce('running', {
      atS: (nowMs - this.measuringFromMs) / 1000,
      frames: frames - this.framesAtHeartbeat,
      worstMs: this.worstSinceHeartbeatMs,
      resident: this.options.terrainStats?.().resident ?? 0,
    });
    this.nextHeartbeatMs = nowMs + HEARTBEAT_INTERVAL_MS;
    this.framesAtHeartbeat = frames;
    this.worstSinceHeartbeatMs = 0;
  }

  /** Fold this frame into the worst-frame list, with everything that happened in it. */
  private recordFrame(nowMs: number, programCount: number): void {
    const frameMs = nowMs - this.lastFrameAtMs;
    this.lastFrameAtMs = nowMs;
    if (!(frameMs > 0)) return;
    if (frameMs > this.worstSinceHeartbeatMs) this.worstSinceHeartbeatMs = frameMs;

    const marks = scratchMarks;
    this.readMarks(marks);
    const tilesAdopted = marks.tilesAdopted - this.previousMarks.tilesAdopted;
    const uploadedBytes = marks.uploadedBytes - this.previousMarks.uploadedBytes;
    const materialsCreated = marks.materialsCreated - this.previousMarks.materialsCreated;
    copyMarks(marks, this.previousMarks);

    /*
     * Differenced every frame, *before* the rejection below.
     *
     * The rejection returns early for the ~99 % of frames that are not slow, so anything tracked
     * after it would compare against whenever the last slow frame happened rather than against
     * the previous frame, and every long frame would be credited with every recompile since the
     * last one. That would turn the most useful column on the table into a running total.
     */
    // Differenced every frame, before the rejection below, for the same reason as the shader
    // counter: after it, a long frame would be credited with every rebase since the last one.
    let rebases = 0;
    const rebaseCount = this.options.originRebases?.() ?? -1;
    if (rebaseCount >= 0) {
      if (this.previousRebaseCount >= 0) {
        rebases = Math.max(0, rebaseCount - this.previousRebaseCount);
      }
      this.previousRebaseCount = rebaseCount;
    }

    let programsCompiled = 0;
    if (programCount >= 0) {
      if (this.previousProgramCount >= 0) {
        programsCompiled = Math.max(0, programCount - this.previousProgramCount);
      }
      this.previousProgramCount = programCount;
    }

    // Cheap rejection first: most frames are not interesting, and the worst list is sorted.
    if (
      this.longFrames.length === LONG_FRAME_COUNT &&
      frameMs <= this.longFrames[LONG_FRAME_COUNT - 1].frameMs
    ) {
      return;
    }

    const profiler = this.options.profiler;
    const physicsMs = profiler.channelMs(Channel.Physics);
    const terrainMs = profiler.channelMs(Channel.Terrain);
    const renderCpuMs = profiler.channelMs(Channel.RenderCPU);
    const audioMs = profiler.channelMs(Channel.Audio);
    const gapMs = Math.max(0, frameMs - (physicsMs + terrainMs + renderCpuMs + audioMs));
    const phases = this.options.renderPhases?.();

    const entry: LongFrame = {
      atS: (nowMs - this.measuringFromMs) / 1000,
      frameMs,
      physicsMs,
      terrainMs,
      renderCpuMs,
      sceneMs: phases?.sceneMs ?? 0,
      cloudsMs: phases?.cloudsMs ?? 0,
      postMs: phases?.postMs ?? 0,
      rebases,
      gapMs,
      tilesAdopted,
      uploadedBytes,
      materialsCreated,
      programsCompiled,
      suspect: classify(
        frameMs,
        physicsMs,
        terrainMs,
        renderCpuMs,
        gapMs,
        tilesAdopted,
        materialsCreated,
        programsCompiled,
        phases,
        rebases,
      ),
    };

    // Insertion sort into a 20-element list: cheaper than sorting, and it allocates nothing
    // beyond the one entry we already decided is worth keeping.
    let i = this.longFrames.length;
    this.longFrames.push(entry);
    while (i > 0 && this.longFrames[i - 1].frameMs < entry.frameMs) {
      this.longFrames[i] = this.longFrames[i - 1];
      i--;
    }
    this.longFrames[i] = entry;
    if (this.longFrames.length > LONG_FRAME_COUNT) this.longFrames.length = LONG_FRAME_COUNT;
  }

  private sampleHeap(nowMs: number): void {
    const heapBytes = heapUsedBytes();
    if (heapBytes === 0) return; // not Chrome; the trace would be a flat line of zeros
    const heapMb = heapBytes / (1024 * 1024);

    if (heapMb > this.bucketPeakMb) this.bucketPeakMb = heapMb;
    if (heapMb < this.bucketFloorMb) this.bucketFloorMb = heapMb;
    if (nowMs < this.bucketEndsAtMs) return;

    this.bucketEndsAtMs = nowMs + 1_000;
    const terrain = this.options.terrainStats?.();
    this.heapTrace.push({
      atS: (nowMs - this.measuringFromMs) / 1000,
      heapMb: this.bucketPeakMb,
      floorMb: this.bucketFloorMb,
      resident: terrain?.resident ?? 0,
      // Read at the same instant as the floor, so the subtraction lines up. See heapGrowthFromTrace.
      tileHeapMb: (terrain?.heapBytes ?? 0) / (1024 * 1024),
    });
    this.bucketPeakMb = 0;
    this.bucketFloorMb = Infinity;
  }

  /**
   * Heap growth over the run [MB]: the difference between the collected floor early in the
   * window and the collected floor at the end. Falls back to the raw endpoint delta when the
   * trace is too short to have floors (a very short run, or a browser without `performance.memory`).
   */
  /**
   * Growth measured from one *post-collection floor* to another.
   *
   * ## Why the obvious version is wrong
   *
   * This used to compare the lowest second in the first quarter of the run against the lowest in
   * the last quarter, on the reasoning that a floor is what survived collection and a floor that
   * has moved is a leak. That is the right idea and the wrong implementation, because it assumes
   * every quarter *contains* a collection.
   *
   * A real canyon run allocates hard enough to sawtooth between about 92 MB and 180 MB with roughly
   * eleven seconds between major collections. In a 45 s window that is three collections, and
   * whether the final quarter holds one is very nearly a coin toss. On the run that exposed this it
   * did not: the last collection landed at t ≈ 29 s and the final eleven seconds were pure rising
   * edge, so the "floor" it compared against was simply the lowest point on a ramp. It reported
   * 29.6 MB of growth. The actual post-collection floors across that same run were 95.1, 96.2,
   * 93.7, 92.1, 92.0, 91.9 and 95.3 MB — flat, i.e. nothing leaking at all.
   *
   * ## What it does instead
   *
   * Find the collections — a floor that drops sharply is one — and compare the floor left by the
   * first against the floor left by the last. That is the only comparison that means "what
   * survived", and it is immune to where in the sawtooth the window happened to close.
   *
   * With fewer than two collections there is no trend to measure, and the honest answer is to say
   * so rather than to report the difference between two arbitrary points on a ramp. The caller
   * turns that into a metric with no verdict, exactly as `hostLoad.mjs` withholds a verdict on a
   * contended host.
   */
  /**
   * Growth measured from one post-collection floor to another.
   *
   * Delegates the rule to {@link heapGrowthFromTrace} and adds the one thing a pure function
   * cannot know: what to say when the run was too short to have a trace at all, where the only
   * available answer is the live heap against the baseline.
   */
  private heapGrowth(): HeapGrowth {
    const growth = heapGrowthFromTrace(this.heapTrace);
    if (this.heapTrace.length >= MIN_HEAP_TRACE_SAMPLES) return growth;
    return {
      ...growth,
      mb:
        this.heapBaselineBytes === 0
          ? 0
          : (heapUsedBytes() - this.heapBaselineBytes) / (1024 * 1024),
    };
  }

  /**
   * Has the terrain stopped arriving?
   *
   * True once nothing is pending adoption and the resident count has held still for
   * {@link SETTLED_FRAMES} consecutive frames — or once {@link maxWarmupMs} has elapsed, in which
   * case it gives up and lets the measurement start anyway, recording that it did.
   *
   * The resident count is watched as well as the pending count because they fail differently: a
   * scene can have nothing pending for a frame simply because the fetch queue is between batches,
   * while tiles are still very much arriving.
   */
  private streamingSettled(nowMs: number): boolean {
    if (nowMs - this.startedAtMs >= this.maxWarmupMs) {
      this.warmupSettledValue = false;
      return true;
    }

    const terrain = this.options.terrainStats?.();
    if (terrain === undefined) return true;

    const marks = scratchMarks;
    this.readMarks(marks);
    /*
     * `loading` and `queued`, **not** `pendingAdoptions`.
     *
     * The first version of this gate watched `pendingAdoptions`, which counts tiles waiting to be
     * adopted into the scene — and that is zero while hundreds of tiles are still being fetched
     * over the network. So a scene where nothing had arrived at all read as "settled", the gate
     * passed on the first frame it was asked, and it did exactly nothing. The run it was written
     * to fix reported `warm-up done after 10.50s` with 243 tiles still in flight.
     *
     * The resident count is watched too, because the three counters can all be momentarily zero
     * between fetch batches while tiles are very much still arriving.
     */
    const quiet =
      terrain.loading === 0 &&
      terrain.queued === 0 &&
      marks.pendingAdoptions === 0 &&
      marks.resident === this.settledResident;
    this.settledResident = marks.resident;
    this.settledFrames = quiet ? this.settledFrames + 1 : 0;
    if (this.settledFrames < SETTLED_FRAMES) return false;

    this.warmupSettledValue = true;
    return true;
  }

  private readMarks(out: StreamingMarks): void {
    const marks = this.options.streamingMarks?.();
    if (marks === undefined) {
      copyMarks(emptyMarksSingleton, out);
      return;
    }
    copyMarks(marks, out);
  }

  private finish(nowMs: number): void {
    this.finished = true;
    const { profiler, quality, backend } = this.options;

    const durationS = (nowMs - this.measuringFromMs) / 1000;
    const frames = profiler.frameCount - this.framesAtStart;

    // Measured floor-to-floor, not endpoint-to-endpoint. A steady scene allocates and collects
    // all the time, so a single reading at the end lands wherever the sawtooth happens to be and
    // the same healthy engine can score anywhere across a 20 MB range run to run. What the
    // budget is actually about is what *survives* collection, which is the floor — and using it
    // makes the number both honest and repeatable.
    const heap = this.heapGrowth();
    const heapDeltaMb = heap.mb;

    /*
     * Where the garbage came from, when the probe was asked for.
     *
     * Reported beside the heap figure rather than as a budget: these are lower bounds (see
     * `AllocationProbe`), and a lower bound must not be allowed to fail a run. It is here to point
     * at a subsystem, not to police one.
     */
    const probe = this.options.allocationProbe;
    const allocation: AllocationBreakdown[] | null =
      probe === undefined || probe.frames === 0
        ? null
        : ALLOCATION_SLOT_NAMES.map((channel, slot) => ({
            channel,
            bytesPerFrame: probe.bytesPerFrame(slot),
          })).sort((a, b) => b.bytesPerFrame - a.bytesPerFrame);

    const averageFps = profiler.averageFps;
    const onePercentLowFps = profiler.lowFps();
    const physicsMs = profiler.channelSmoothedMs(Channel.Physics);
    const terrainMs = profiler.channelSmoothedMs(Channel.Terrain);
    const renderCpuMs = profiler.channelSmoothedMs(Channel.RenderCPU);
    const audioMs = profiler.channelSmoothedMs(Channel.Audio);
    // Tiles gained across the window, from the heap trace's own per-sample resident count. Zero
    // when the window opened on a settled stream, which is when the heap verdict can be believed.
    const residentDelta =
      this.heapTrace.length === 0
        ? 0
        : this.heapTrace[this.heapTrace.length - 1].resident - this.heapTrace[0].resident;

    const metrics: PerfMetric[] = [
      metric('avg fps', averageFps, 60, 'fps', averageFps >= 60),
      metric('1% low fps', onePercentLowFps, 55, 'fps', onePercentLowFps >= 55),
      metric('frame time', profiler.averageFrameMs, 16.67, 'ms', profiler.averageFrameMs <= 16.67),
      metric(CHANNEL_NAMES[Channel.Physics], physicsMs, CHANNEL_BUDGET_MS[Channel.Physics], 'ms', physicsMs <= CHANNEL_BUDGET_MS[Channel.Physics]),
      metric(CHANNEL_NAMES[Channel.Terrain], terrainMs, CHANNEL_BUDGET_MS[Channel.Terrain], 'ms', terrainMs <= CHANNEL_BUDGET_MS[Channel.Terrain]),
      metric(CHANNEL_NAMES[Channel.RenderCPU], renderCpuMs, CHANNEL_BUDGET_MS[Channel.RenderCPU], 'ms', renderCpuMs <= CHANNEL_BUDGET_MS[Channel.RenderCPU]),
      metric(CHANNEL_NAMES[Channel.Audio], audioMs, CHANNEL_BUDGET_MS[Channel.Audio], 'ms', audioMs <= CHANNEL_BUDGET_MS[Channel.Audio]),
      metric('draw calls', this.peakDrawCalls, 350, '', this.peakDrawCalls < 350),
      metric('triangles', this.peakTriangles, 2_500_000, '', this.peakTriangles < 2_500_000),
      // A steady scene must not grow the heap: this is the "flat heap" acceptance criterion.
      /*
       * No verdict when the run did not collect at least twice: see {@link heapGrowth}. Reporting
       * a pass or a fail from a mid-sawtooth reading is the same lie `hostLoad.mjs` refuses to
       * tell about a contended host, and it cost this milestone a day of chasing a leak that was
       * not there.
       */
      heap.conclusive
        ? metric('heap delta', heapDeltaMb, 8, 'MB', heapDeltaMb <= 8)
        : metric('heap span (no verdict)', heapDeltaMb, null, 'MB', true),
      /*
       * The confound, reported rather than hidden. `heap delta` above has this subtracted out; a
       * reader who wants the raw floor-to-floor figure the old rule printed can add the two back
       * together. A large number here is not a fault — it is the tile cache doing its job — but it
       * says the window opened while the cache was still filling, which is worth seeing.
       */
      metric('tile heap Δ (excluded)', heap.tileHeapMb, null, 'MB', true),
      /*
       * How many tiles the stream added *during the measurement window* — the condition under
       * which the heap verdict above is worth least.
       *
       * Measured across four runs of one build. The two whose windows opened on a settled stream
       * (Δ = 0) scored **−5.47 and −2.15 MB**: tight, and comfortably inside budget. The two that
       * opened mid-fill (Δ = +101 tiles, 229 → 330) scored **+9.67 and +3.10 MB** — a spread three
       * times wider, straddling the 8 MB budget, and that is *after* 13.04 MB of tile bytes had
       * already been subtracted out both times.
       *
       * So this is not a predictor of failure — one filling window passed — it is a predictor of
       * *variance*. The reading is that the cache's own geometry is not all the stream retains:
       * three builds a `RenderObject` and its bind groups for every tile first drawn, and those
       * live on its side of the fence where `TerrainStats.heapBytes` cannot see them, so a window
       * that spans the fill carries an unattributed term that the subtraction cannot remove.
       *
       * The root cause is upstream of the heap rule: the warm-up's streaming gate said "settled"
       * in all three runs, and in the third it was wrong, because `resident` plateaus between LOD
       * steps and `pendingAdoptions` dips to zero between fetch batches. Whether it is right
       * depends mostly on how warm the tile cache was from the previous run — which is exactly the
       * shape of the original two-runs-of-one-build discrepancy this whole thread started from.
       *
       * Reported rather than acted on. Tightening the warm-up gate is a rule change, it wants a
       * quiet host to validate, and this session has already shipped one unvalidated gate and had
       * to take it back out again.
       */
      metric('tiles resident Δ', residentDelta, null, '', true),
    ];

    /*
     * Only judged when the window actually caught a rebase. A run that never moved the origin has
     * nothing to say about rebase frames, and reporting a triumphant 0.0 ms would be the same lie
     * as reporting a heap verdict from a window with no collections in it.
     */
    const worstRebaseMs = worstRebaseFrameMs(this.longFrames);
    if (worstRebaseMs > 0) {
      metrics.push(
        metric(
          REBASE_FRAME_METRIC,
          worstRebaseMs,
          REBASE_FRAME_BUDGET_MS,
          'ms',
          worstRebaseMs <= REBASE_FRAME_BUDGET_MS,
        ),
      );
    }

    if (this.options.terrainStats !== undefined) {
      const terrain = this.options.terrainStats();
      // The milestone's acceptance criterion, as a number: the tile cache must stay inside its
      // budget over a sustained flight rather than growing until the tab is killed.
      const cacheMb = terrain.cacheBytes / (1024 * 1024);
      const budgetMb = terrain.cacheMaxBytes / (1024 * 1024);
      metrics.push(
        metric('tile cache', cacheMb, budgetMb, 'MB', cacheMb <= budgetMb * 1.02),
        metric('tiles resident', terrain.resident, null, '', true),
        metric('tile failures', terrain.failed, 0, '', terrain.failed === 0),
      );
    }

    const result: PerfResult = {
      scene: this.options.sceneName,
      backend,
      tier: quality.tier,
      durationS,
      warmupS: this.options.warmupS,
      frames,
      averageFps,
      onePercentLowFps,
      averageFrameMs: profiler.averageFrameMs,
      physicsMs,
      terrainMs,
      renderCpuMs,
      drawCalls: this.peakDrawCalls,
      triangles: this.peakTriangles,
      heapDeltaMb,
      /** How many major collections the window caught. Under two, the heap figure has no verdict. */
      heapCollections: heap.collections,
      /** False when the warm-up gave up waiting for the terrain rather than seeing it settle. */
      warmupSettled: this.warmupSettledValue,
      ...(allocation === null ? {} : { allocation }),
      longFrames: this.longFrames,
      heapTrace: this.heapTrace,
      metrics,
      passed: metrics.every((m) => m.pass !== false),
    };

    this.resultValue = result;
    publish(result);
    this.options.onComplete?.(result);
  }
}

/**
 * The one-word explanation for a slow frame.
 *
 * Order matters: a frame that built a material was almost certainly compiling a pipeline, and
 * that dwarfs whatever else it also did. An unattributed gap that is most of the frame is a
 * collection or a driver stall, because every piece of work this engine does is inside a channel.
 */
function classify(
  frameMs: number,
  physicsMs: number,
  terrainMs: number,
  renderCpuMs: number,
  gapMs: number,
  tilesAdopted: number,
  materialsCreated: number,
  programsCompiled: number,
  phases?: { sceneMs: number; cloudsMs: number; postMs: number },
  rebases = 0,
): string {
  /*
   * Shaders first, ahead of everything.
   *
   * A recompile is invisible in every other column — it is render CPU with nothing to blame it
   * on — and it is the most expensive thing a frame can do, so when one happened it is the
   * explanation, not a contributing factor. Ahead of `pipeline` (terrain material creation)
   * because that is a *cause* of recompiles and this is the recompile itself.
   */
  /*
   * Ahead of everything, including the shader counter: a rebase frame compiles nothing, adopts
   * nothing and builds nothing, so every other rule in this function would blame it on "render"
   * and stop there. It is the one cause the itemisation could not otherwise name.
   */
  if (rebases > 0) return 'origin rebase';
  if (programsCompiled > 0) return `shader ×${programsCompiled}`;
  if (materialsCreated > 0) return 'pipeline';
  if (gapMs > frameMs * 0.6) return 'gc/stall';
  if (tilesAdopted > 0) return 'tile upload';
  if (terrainMs > renderCpuMs && terrainMs > physicsMs) return 'streaming';
  if (renderCpuMs > physicsMs) {
    // Name the pass rather than the call site, when the phase timer was on. "render" is where the
    // milliseconds were charged; "render:scene" is where they were spent.
    if (phases !== undefined) {
      const { sceneMs, cloudsMs, postMs } = phases;
      const worst = Math.max(sceneMs, cloudsMs, postMs);
      if (worst > renderCpuMs * 0.5) {
        return worst === sceneMs ? 'render:scene' : worst === cloudsMs ? 'render:clouds' : 'render:post';
      }
    }
    return 'render';
  }
  if (physicsMs > 0) return 'physics';
  return 'unknown';
}

/** A result with nothing measured in it — the shape an aborted run reports. */
function blankResult(scene: string, backend: RendererBackend, tier: string): PerfResult {
  return {
    scene,
    backend,
    tier,
    durationS: 0,
    warmupS: 0,
    frames: 0,
    averageFps: 0,
    onePercentLowFps: 0,
    averageFrameMs: 0,
    physicsMs: 0,
    terrainMs: 0,
    renderCpuMs: 0,
    drawCalls: 0,
    triangles: 0,
    heapDeltaMb: 0,
    longFrames: [],
    heapTrace: [],
    metrics: [],
    passed: false,
  };
}

function emptyMarks(): StreamingMarks {
  return {
    tilesAdopted: 0,
    uploadedBytes: 0,
    materialsCreated: 0,
    pendingAdoptions: 0,
    resident: 0,
  };
}

function copyMarks(from: StreamingMarks, to: StreamingMarks): void {
  to.tilesAdopted = from.tilesAdopted;
  to.uploadedBytes = from.uploadedBytes;
  to.materialsCreated = from.materialsCreated;
  to.pendingAdoptions = from.pendingAdoptions;
  to.resident = from.resident;
}

/** Reused so per-frame sampling allocates nothing — the harness must not perturb what it measures. */
const scratchMarks: StreamingMarks = /* #__PURE__ */ emptyMarks();
const emptyMarksSingleton: StreamingMarks = /* #__PURE__ */ emptyMarks();

function metric(
  name: string,
  value: number,
  budget: number | null,
  unit: string,
  pass: boolean,
): PerfMetric {
  return { metric: name, value, budget, unit, pass: budget === null ? null : pass };
}

/**
 * Tell the runner something happened, even if the run never finishes.
 *
 * A perf run that goes silent is the worst thing this tool can do: `tools/perf.mjs` waits out its
 * whole timeout and then reports nothing at all, which says only "something, somewhere, did not
 * work". These progress posts turn that into a location — a run that never announces `started`
 * failed before the loop; one that announces `started` but not `measuring` died in the warm-up.
 *
 * Fire-and-forget, and deliberately unawaited: this is called from the frame path.
 */
export function announce(phase: string, detail: Record<string, unknown> = {}): void {
  post({ phase, ...detail });
}

/**
 * Send a payload to the runner, and actually check that it went.
 *
 * `navigator.sendBeacon` returns `false` — it does not throw — when the payload is over the user
 * agent's limit or the queue is full, so the `try/catch` this used to rely on caught nothing and a
 * refused beacon was indistinguishable from a successful one. The final result carries twenty long
 * frames and up to sixty heap samples, which is exactly the size that starts running into it.
 * `fetch` with `keepalive` is the documented fallback and survives the page going away.
 */
function post(payload: unknown): void {
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon?.(PERF_ENDPOINT, body) === true) return;
  } catch {
    // Fall through to fetch.
  }
  void fetch(PERF_ENDPOINT, {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'content-type': 'application/json' },
  }).catch(() => {
    // No runner listening — the console output and the global are enough.
  });
}

/** Console table, global, and a beacon for `tools/perf.mjs`. */
function publish(result: PerfResult): void {
  (globalThis as { __SKYSIM_PERF__?: PerfResult }).__SKYSIM_PERF__ = result;

  console.log(
    `%c${PRODUCT_NAME} perf — ${result.scene} · ${result.backend} · ${result.tier} · ${result.frames} frames in ${result.durationS.toFixed(1)}s (after a ${result.warmupS}s warm-up)`,
    'font-weight:bold',
  );
  console.table(
    result.metrics.map((m) => ({
      metric: m.metric,
      value: `${m.value.toFixed(2)}${m.unit ? ` ${m.unit}` : ''}`,
      budget: m.budget === null ? '—' : `${m.budget}${m.unit ? ` ${m.unit}` : ''}`,
      status: m.pass === null ? '—' : m.pass ? 'PASS' : 'FAIL',
    })),
  );
  if (result.longFrames.length > 0) {
    console.log('%c20 longest frames', 'font-weight:bold');
    console.table(
      result.longFrames.map((f) => ({
        't (s)': f.atS.toFixed(1),
        frame: `${f.frameMs.toFixed(1)} ms`,
        gap: `${f.gapMs.toFixed(1)} ms`,
        tiles: f.tilesAdopted,
        'upload KB': (f.uploadedBytes / 1024).toFixed(0),
        materials: f.materialsCreated,
        suspect: f.suspect,
      })),
    );
  }
  if (result.abortedBecause !== undefined) {
    console.warn(`${PRODUCT_NAME} perf — run abandoned: ${result.abortedBecause}`);
  }
  console.log(result.passed ? '%cAll budgets met.' : '%cBudget exceeded.', 'font-weight:bold');

  post(result);
}
